import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { UpgradeSheet } from '../components/upgrade/UpgradeSheet';

interface OpenOptions {
  /** Pre-select the plan toggle. Default: 'pro'. */
  defaultPlan?: 'pro' | 'pro-family' | undefined;
}

interface UpgradeSheetContextValue {
  /** Open the global upgrade sheet. Optional `trigger` string is
   *  shown as a small eyebrow above the title — typically the gated
   *  feature that prompted the open (e.g. "Opening hours"). The
   *  second argument lets a Family-flavored callsite (e.g. the
   *  /family upsell card) pre-select the Family plan in the toggle. */
  open: (trigger?: string | undefined, options?: OpenOptions | undefined) => void;
  close: () => void;
}

const UpgradeSheetContext = createContext<UpgradeSheetContextValue | undefined>(undefined);

/**
 * Single mount-point for the upgrade flow. Multiple cards, layer
 * toggles, and feature affordances can all open the same sheet
 * without each owning a separate `<UpgradeSheet />` instance — each
 * instance would hold its own checkout-mutation state, render its
 * own (hidden) `Sheet` overlay, and bloat the render tree.
 */
export function UpgradeSheetProvider({ children }: { children: React.ReactNode }) {
  const [trigger, setTrigger] = useState<string | null>(null);
  const [defaultPlan, setDefaultPlan] = useState<'pro' | 'pro-family'>('pro');

  const open = useCallback((next?: string | undefined, options?: OpenOptions | undefined) => {
    // `null` collapses to undefined for the sheet prop, but we
    // distinguish null-as-closed from null-as-open-without-trigger
    // by using empty-string sentinel internally and undefined to
    // the sheet.
    setTrigger(next ?? '');
    setDefaultPlan(options?.defaultPlan ?? 'pro');
  }, []);

  const close = useCallback(() => setTrigger(null), []);

  const value = useMemo<UpgradeSheetContextValue>(() => ({ open, close }), [open, close]);

  return (
    <UpgradeSheetContext.Provider value={value}>
      {children}
      <UpgradeSheet
        open={trigger !== null}
        onClose={close}
        trigger={trigger && trigger.length > 0 ? trigger : undefined}
        defaultPlan={defaultPlan}
      />
    </UpgradeSheetContext.Provider>
  );
}

export function useUpgradeSheet(): UpgradeSheetContextValue {
  const ctx = useContext(UpgradeSheetContext);
  if (!ctx) throw new Error('useUpgradeSheet must be used within UpgradeSheetProvider');
  return ctx;
}
